import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useUser, SignInButton } from '@clerk/clerk-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import useOrgMembership from './hooks/useOrgMembership';
import OrgSetupModal from './components/OrgSetupModal';
import GuestDevicePopup from './components/GuestDevicePopup';
import CustomerRmaModalPending from './components/CustomerRmaModalPending.jsx';
import CustomerRmaModalAssigned from './components/CustomerRmaModalAssigned.jsx';
import CustomerRmaModalReceived from './components/CustomerRmaModalReceived.jsx';
import CustomerRmaModalRepairing from './components/CustomerRmaModalRepairing.jsx';
import CustomerRmaModalReady from './components/CustomerRmaModalReady.jsx';
import RmaModalShipped from './components/RmaModalShipped.jsx';
import CustomerRmaModalShipped from './components/CustomerRmaModalShipped.jsx';
import CustomerRmaModalClosedCase from './components/CustomerRmaModalClosedCase.jsx';
import WarrantyModal from './components/WarrantyModal.jsx';
import DimmingDataModal from './components/DimmingDataModal.jsx';
import { colors } from './theme';
import { buildDeviceFields } from './utils/deviceFields';
import { getWarrantyStatus, getWarrantyColor } from './warrantyUtils';
import { getOrgWarrantyExtra } from './warrantyAgreements';
import { parseDateInput } from './utils/dates';
import RmaTimelineCard from './components/RmaTimelineCard.jsx';
import LoadingSpinner from './components/LoadingSpinner';
import { getProductPdf } from './productFiles';
import { computeDataQualityScore } from './dataQualityUtils';
import { normalizeRoleToken } from './roles';
import GpvRmaFollowupPanel from './components/GpvRmaFollowupPanel.jsx';
import DeviceArchivePanel from './components/DeviceArchivePanel.jsx';
import NorthstarHistory from './components/NorthstarHistory';
import GuestRmaLookup from './components/GuestRmaLookup.jsx';
import { createAuthenticatedProvider, createPublicProvider, createGuestProvider } from './api/deviceDataProvider.js';
import ProductThumb from './components/ProductThumb';
import {
  SERIAL_POLICY_DEFAULT,
  SHIPMENT_TYPE_DEFAULT,
  MOUNT_TYPE_OPTIONS,
  MOUNT_TYPE_DEFAULT,
  ACCESSORY_FIELDS
} from './rmaFieldUtils';

const createEmptyContext = () => {
  const context = () => null;
  context.keys = () => [];
  return context;
};

let dimmingContext = createEmptyContext();
try {
  dimmingContext = require.context('./product-images', true, /\/fileset\//);
} catch (err) {
  // ignore missing context in non-webpack environments
}


const API_BASE = process.env.REACT_APP_API_URL;

// Rows in the "more than one device" list on the RMA request form (issue #712).
const extraDeviceRowStyle = {
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  padding: '0.55rem 0.7rem',
  marginBottom: '0.5rem',
  background: 'rgba(255,255,255,0.03)',
};
const extraDeviceIndexStyle = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#46bb89',
  letterSpacing: '0.04em',
};

// ─── Warranty claim bonus ────────────────────────────────────────────────────
// Change this one value to update every label, button, and calculation at once.
// Must match WARRANTY_CLAIM_MONTHS in backend/routes/analytics.js.
const CLAIM_WARRANTY_BONUS_MONTHS = 1;
const CLAIM_WARRANTY_LABEL = CLAIM_WARRANTY_BONUS_MONTHS % 12 === 0
  ? `+${CLAIM_WARRANTY_BONUS_MONTHS / 12} yr`
  : `+${CLAIM_WARRANTY_BONUS_MONTHS} mo`;

const CHALLENGE_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const CHALLENGE_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.pdf'];
const HEIC_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSIONS = new Set(['.heic', '.heif']);
const CHALLENGE_MAX_FILES = 5;
const CHALLENGE_MAX_FILE_SIZE = 10 * 1024 * 1024;

const ASSIGNED_OPTIONS = [
  'in field',
  'decommissioned',
  'trade fairs',
  'customer testing'
];

const expectedFormStyles = {
  container: {
    border: `1px solid ${colors.panelBorder}`,
    borderRadius: '16px',
    padding: '1rem',
    marginBottom: '0.75rem',
    backgroundColor: colors.surfaceRaised,
    color: colors.textBody
  },
  sectionTitle: {
    margin: '0 0 0.5rem',
    fontSize: '1.05rem',
    fontWeight: 600,
    color: colors.textStrong
  },
  meta: {
    margin: '0 0 0.5rem',
    fontSize: '0.9rem',
    color: colors.textMuted
  },
  label: {
    fontWeight: 600,
    marginBottom: '0.25rem',
    display: 'block',
    color: colors.textStrong
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    margin: '0.5rem 0',
    color: colors.textBody
  },
  choiceGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.5rem'
  },
  choiceButton: {
    border: `1px solid ${colors.panelBorder}`,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderRadius: '999px',
    padding: '0.45rem 0.9rem',
    cursor: 'pointer',
    color: colors.textBody,
    fontWeight: 600
  },
  choiceButtonActive: {
    backgroundColor: colors.primary,
    color: '#11201c',
    borderColor: colors.primary
  },
  mountGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '0.5rem'
  },
  subField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    minWidth: '120px'
  },
  numberInput: {
    width: '100%',
    padding: '0.55rem 0.7rem',
    borderRadius: '10px',
    border: `1px solid ${colors.panelBorder}`,
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong
  },
  accessoryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '0.5rem'
  },
  accessoryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem'
  },
  accessoryLabel: {
    fontWeight: 500,
    color: colors.textBody
  },
  accessoryHint: {
    marginLeft: '0.35rem',
    fontSize: '0.8rem',
    color: colors.textDim
  }
};

const deviceStyles = {
  page: {
    background:
      'radial-gradient(circle at top left, rgba(70,187,137,0.12), transparent 32%), linear-gradient(180deg, #0a0f0d 0%, #101614 100%)',
    minHeight: '100vh',
    padding: 'clamp(1rem, 2vw, 2rem)'
  },
  shell: {
    maxWidth: '980px',
    margin: '0 auto',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.02), transparent), rgba(17, 22, 20, 0.94)',
    borderRadius: '24px',
    boxShadow: colors.shadow,
    position: 'relative',
    overflow: 'hidden',
    border: `1px solid ${colors.panelBorder}`
  },
  header: {
    padding: '1.35rem 1.5rem 1.1rem',
    borderBottom: `1px solid ${colors.panelBorder}`,
    background: 'linear-gradient(135deg, rgba(70,187,137,0.14), rgba(255,255,255,0.02) 56%)'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  title: {
    margin: 0,
    color: colors.textStrong,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
    fontSize: 'clamp(2rem, 5vw, 3rem)',
    lineHeight: 0.95
  },
  subtitle: {
    margin: '0.25rem 0 0',
    color: colors.textMuted,
    fontSize: '0.92rem'
  },
  qualityBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: colors.textStrong,
    borderRadius: '999px',
    padding: '0.45rem 1rem',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontSize: '0.9rem',
    border: '1px solid rgba(255,255,255,0.18)'
  },
  content: {
    display: 'flex',
    flexWrap: 'wrap',
    padding: '1.5rem',
    gap: '1.5rem'
  },
  imageWrap: {
    flex: '1 1 280px',
    position: 'relative'
  },
  image: {
    width: '100%',
    borderRadius: '18px',
    boxShadow: '0 18px 40px rgba(0,0,0,0.26)',
    transform: 'rotate(-4deg)',
    background: 'rgba(255,255,255,0.02)'
  },
  viewModeWrap: {
    position: 'absolute',
    bottom: '14px',
    left: '14px',
    maxWidth: 'calc(100% - 28px)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.35rem',
    padding: '0.35rem',
    borderRadius: '22px',
    backgroundColor: 'rgba(9, 12, 11, 0.82)',
    border: `1px solid ${colors.panelBorder}`,
    backdropFilter: 'blur(12px)'
  },
  viewModeButton: {
    padding: '0.35rem 0.75rem',
    border: '1px solid transparent',
    borderRadius: '999px',
    backgroundColor: 'transparent',
    color: colors.textMuted,
    fontWeight: 600,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
  },
  viewModeButtonActive: {
    backgroundColor: colors.accent,
    color: '#11201c'
  },
  details: {
    flex: '1 1 280px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.85rem',
    color: colors.textBody
  },
  detailBlock: {
    display: 'grid',
    gap: '0.3rem'
  },
  detailLabel: {
    color: colors.textDim,
    fontSize: '0.78rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: 700
  },
  detailValue: {
    color: colors.textStrong,
    fontSize: '1rem',
    fontWeight: 600
  },
  actionRow: {
    marginTop: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.65rem',
    alignItems: 'center'
  },
  section: {
    padding: '1.5rem',
    borderTop: `1px solid ${colors.panelBorder}`,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.01), transparent)'
  },
  sectionTitle: {
    margin: 0,
    color: colors.textStrong,
    fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
    fontSize: '2rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase'
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(3, 6, 5, 0.78)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '1.25rem'
  },
  modal: {
    width: 'min(92vw, 820px)',
    maxHeight: '88vh',
    overflowY: 'auto',
    position: 'relative',
    background:
      'linear-gradient(180deg, rgba(255,255,255,0.03), transparent), rgba(17, 22, 20, 0.97)',
    border: `1px solid ${colors.panelBorder}`,
    boxShadow: colors.shadow,
    color: colors.textBody,
    borderRadius: '24px',
    padding: '1.5rem'
  },
  modalNarrow: {
    width: 'min(92vw, 560px)'
  },
  modalTitle: {
    margin: '0 0 0.35rem',
    color: colors.textStrong,
    fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
    fontSize: '2rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase'
  },
  modalIntro: {
    margin: 0,
    fontSize: '0.9rem',
    color: colors.textMuted
  },
  closeButton: {
    position: 'absolute',
    top: '0.8rem',
    right: '0.8rem',
    backgroundColor: 'transparent',
    border: 'none',
    color: colors.textStrong,
    fontSize: '1.6rem',
    cursor: 'pointer'
  },
  input: {
    width: '100%',
    marginBottom: '0.7rem',
    borderRadius: '12px',
    border: `1px solid ${colors.panelBorder}`,
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: colors.textStrong,
    padding: '0.75rem 0.9rem'
  },
  label: {
    display: 'block',
    color: colors.textStrong,
    fontWeight: 600,
    marginBottom: '0.35rem'
  },
  helperText: {
    width: '100%',
    margin: 0,
    color: colors.textMuted,
    fontSize: '0.9rem'
  },
  tableRow: {
    borderBottom: `1px solid ${colors.panelBorder}`,
    padding: '0.45rem 0',
    color: colors.textBody
  }
};


const CUSTOMER_MODAL_COMPONENTS = {
  pending: CustomerRmaModalPending,
  initiated: CustomerRmaModalPending,
  assigned: CustomerRmaModalAssigned,
  received: CustomerRmaModalReceived,
  awaiting_repair: CustomerRmaModalRepairing,
  repairing: CustomerRmaModalRepairing,
  awaiting_shipping: CustomerRmaModalReady,
  shipped: CustomerRmaModalShipped,
  archived: CustomerRmaModalShipped,
  canceled_by_customer: CustomerRmaModalRepairing,
  closed_case: CustomerRmaModalClosedCase
};

const SERVICE_MODAL_COMPONENTS = {
  ...CUSTOMER_MODAL_COMPONENTS,
  shipped: RmaModalShipped,
  archived: RmaModalShipped,
};

const STATUS_ALIASES = {
  Pending: 'pending',
  Initiated: 'pending',
  Assigned: 'assigned',
  Received: 'received',
  'Awaiting Repair Start': 'awaiting_repair',
  Assessment: 'awaiting_repair',
  'Repair In Progress': 'repairing',
  Repairing: 'repairing',
  Ready: 'awaiting_shipping',
  'Repair Finished': 'awaiting_shipping',
  'Repair Finished (Back in Stock/Refurbished)': 'awaiting_shipping',
  'Awaiting Shipping Details': 'awaiting_shipping',
  'Awaiting Dispatch Confirmation': 'awaiting_shipping',
  Shipped: 'shipped',
  ArchivedCaseClosed: 'archived',
  Closed: 'archived',
  'Repair Cancelled by Customer': 'canceled_by_customer',
  cancelled: 'archived',
  canceled_by_customer: 'canceled_by_customer',
  closed_case: 'closed_case'
};

const normalizeStatusKey = status => {
  if (!status) return 'pending';
  const alias = STATUS_ALIASES[status];
  if (alias) return alias;
  const lowered = typeof status === 'string' ? status.toLowerCase() : status;
  return STATUS_ALIASES[lowered] || lowered || 'pending';
};

const normalizeRma = rma => {
  if (!rma || typeof rma !== 'object') {
    return rma;
  }
  return {
    ...rma,
    status: normalizeStatusKey(rma.status)
  };
};

function getOperationStats(device) {
  if (!device) return null;
  let operatingHours =
    typeof device.operatingHours === 'number' ? device.operatingHours : null;
  if (operatingHours === null && device.scandate) {
    const sd = parseDateInput(device.scandate);
    if (sd) {
      operatingHours = Math.floor((Date.now() - sd.getTime()) / 3600000);
    }
  }
  const failureCount =
    typeof device.failureCount === 'number' ? device.failureCount : 0;
  return { operatingHours, failureCount };
}

// Customer-visible overview of the product's overarching characteristics,
// sourced from its part-number metadata (device.productProfile).
function ProductMetadataView({ profile, productNumber }) {
  const p = profile || {};
  const touchLabel = p.touch === true ? 'Touch device' : p.touch === false ? 'Non-touch device' : null;
  const rows = [
    ['Product', productNumber],
    ['Name', p.name],
    ['Family', p.family],
    ['Size', p.size],
    ['Display', touchLabel],
    ['Power supply', p.powerSupply],
    ['Bonding', p.bonding ? `${p.bonding} bonding` : ''],
    ['Brightness', p.brightness],
    ['Interfaces', p.interfaces],
  ].filter(([, v]) => v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <h2 style={deviceStyles.sectionTitle}>Product metadata</h2>
      {touchLabel && (
        <p style={{ margin: 0, color: colors.textBody }}>
          This is a <strong style={{ color: colors.textStrong }}>{p.touch ? 'touch' : 'non-touch'}</strong> device
          {p.powerSupply ? <> with a <strong style={{ color: colors.textStrong }}>{p.powerSupply}</strong> power supply</> : null}.
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
            <span style={{ fontSize: '0.92rem', color: colors.textBody }}>{value}</span>
          </div>
        ))}
      </div>
      {rows.length <= 2 && (
        <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.85rem' }}>
          No detailed characteristics recorded for this product yet.
        </p>
      )}
    </div>
  );
}

export default function DevicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [device, setDevice] = useState(null);
  const [error, setError] = useState(null);

  // Org & claim state
  const { org, loading: orgLoading, joinOrg } = useOrgMembership();
  const [showOrgSetup, setShowOrgSetup] = useState(false);
  const [claimState, setClaimState] = useState(null); // { claimedByMyOrg, otherClaimants, totalClaims }
  const [claiming, setClaiming] = useState(false);
  const [showClaimLocationModal, setShowClaimLocationModal] = useState(false);
  const [claimCountry, setClaimCountry] = useState('');
  const [claimError, setClaimError] = useState('');
  const [showHamburger, setShowHamburger] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [showGuestRmaPrompt, setShowGuestRmaPrompt] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');
  const [issueType, setIssueType] = useState('hardware');
  const [description, setDescription] = useState('');
  const [customerExpectation, setCustomerExpectation] = useState('');
  const [contactOrg, setContactOrg] = useState('');
  const [partialShipmentWarning, setPartialShipmentWarning] = useState(null);
  const [preScreened, setPreScreened] = useState(null);
  const [screeningNotes, setScreeningNotes] = useState('');
  const [formStatus, setFormStatus] = useState('');
  const [rmaImages, setRmaImages] = useState([]);
  // Extra units on the same RMA (issue #712): the customer adds them by service
  // tag, each verified against the device registry before it can be attached.
  const [extraDevices, setExtraDevices] = useState([]);
  const [extraTagInput, setExtraTagInput] = useState('');
  const [extraTagChecking, setExtraTagChecking] = useState(false);
  const [extraTagError, setExtraTagError] = useState('');
  const [showExtraDevices, setShowExtraDevices] = useState(false);
  // Survives the form unmounting on success so the customer sees what was sent.
  const [submittedDeviceCount, setSubmittedDeviceCount] = useState(0);
  const [expectedShipmentType, setExpectedShipmentType] = useState(SHIPMENT_TYPE_DEFAULT);
  const [expectedMountIncluded, setExpectedMountIncluded] = useState(false);
  const [expectedMountType, setExpectedMountType] = useState(MOUNT_TYPE_DEFAULT);
  const [expectedMountHmi, setExpectedMountHmi] = useState(true);
  const [expectedAccessoriesIncluded, setExpectedAccessoriesIncluded] = useState(false);
  const [expectedAccessories, setExpectedAccessories] = useState({
    acPowerCableQty: 0,
    hdmiCableQty: 0,
    dpCableQty: 0,
    touchCableQty: 0
  });

  const sanitizeNonNegativeInt = value => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return Math.round(parsed);
  };

  const handleExpectedAccessoryChange = (key, value) => {
    const qty = sanitizeNonNegativeInt(value);
    setExpectedAccessories(prev => ({
      ...prev,
      [key]: qty
    }));
  };

  const deviceSerialNumber = device?.serialNumber || 'Unknown';
  const deviceProductNumber = device?.productNumber || 'Not available';

  const resetExpectedReturn = () => {
    setExpectedShipmentType(SHIPMENT_TYPE_DEFAULT);
    setExpectedMountIncluded(false);
    setExpectedMountType(MOUNT_TYPE_DEFAULT);
    setExpectedMountHmi(true);
    setExpectedAccessoriesIncluded(false);
    setExpectedAccessories({
      acPowerCableQty: 0,
      hdmiCableQty: 0,
      dpCableQty: 0,
      touchCableQty: 0
    });
  };


  const [showImprovementForm, setShowImprovementForm] = useState(false);
  const [impName, setImpName] = useState('');
  const [impEmail, setImpEmail] = useState('');
  const [impCategory, setImpCategory] = useState('ui');
  const [impDescription, setImpDescription] = useState('');
  const [impStatus, setImpStatus] = useState('');
  const [rmaList, setRmaList] = useState([]);
  const [viewMode, setViewMode] = useState('current'); // 'current', 'archive', 'stats'
  const [showProductData, setShowProductData] = useState(false);
  const [isProductButtonFocused, setIsProductButtonFocused] = useState(false);
  const [manualUrl, setManualUrl] = useState(null);
  const [manualAvailable, setManualAvailable] = useState(false);
  const [showWarrantyPopup, setShowWarrantyPopup] = useState(false);
  const [showDimmingModal, setShowDimmingModal] = useState(false);
  const [activeRma, setActiveRma] = useState(null);
  const [challengeTarget, setChallengeTarget] = useState(null);
  const [challengeMessage, setChallengeMessage] = useState('');
  const [challengeEmail, setChallengeEmail] = useState('');
  const [challengeFiles, setChallengeFiles] = useState([]);
  const [challengeErrors, setChallengeErrors] = useState({});
  const [challengeError, setChallengeError] = useState('');
  const [challengeSubmittingId, setChallengeSubmittingId] = useState(null);
  const [customerDecisionState, setCustomerDecisionState] = useState({});
  const [declinePathState, setDeclinePathState] = useState({});
  const archiveRefetchRef = useRef(() => {});
  const [publicToken, setPublicToken] = useState('');
  const [tokenInitialized, setTokenInitialized] = useState(isSignedIn);

  const [dimmingAccessible, setDimmingAccessible] = useState(null);
  const [dimmingFiles, setDimmingFiles] = useState([]);
  const [publicRmaMeta, setPublicRmaMeta] = useState({
    currentStatus: '',
    hasCustomerShipment: false,
    currentRmaId: null,
    highlightRmaId: null,
    accessScope: 'rma'
  });

  const role = user?.publicMetadata?.role || 'guest';
  const normalizedRole = normalizeRoleToken(role) || 'guest';
  const isAdmin = normalizedRole === 'admin';
  const isServiceLike = normalizedRole.startsWith('service');
  const isGpvRma = normalizedRole === 'gpv-rma';
  const isGpv = normalizedRole === 'gpv';
  const isServiceUser = isAdmin || isServiceLike || isGpvRma || isGpv;
  const isCustomer = isSignedIn && !isServiceUser;
  const isGuestView = !isSignedIn;
  const provider = useMemo(() => {
    if (isSignedIn) {
      return createAuthenticatedProvider({ apiBase: API_BASE });
    }
    if (publicToken) {
      return createPublicProvider({ apiBase: API_BASE, token: publicToken });
    }
    if (tokenInitialized) {
      return createGuestProvider({ apiBase: API_BASE });
    }
    return null;
  }, [isSignedIn, publicToken, tokenInitialized]);
  const showArchived = viewMode === 'archive' && isSignedIn;
  const showStats = viewMode === 'stats';
  const showMeta = viewMode === 'metadata';

  const fieldLabels = {
    serviceTagNumber: "Service Tag Number",
    productNumber: "Product Number",
    model: "Model",
    workorder: "Workorder",
    summary: "Summary",
    scandate: "Scandate",
    scanby: "Scan By",
    testStatus: "Test Status",
    testDate: "Test Date",
    testBy: "Tested By",
    firmware: "Firmware",
    firmwareDate: "Firmware Date",
    firmwareBy: "Firmware By",
    poNumber: "PO Number",
    saleOrder: "Sale Order",
    drawingDIU: "Drawing DIU",
    workDIU: "Work DIU",
    cusPnDIU: "Customer PN DIU",
    snDisplayCabinet: "SN Display Cabinet",
    snDisplay: "SN Display",
    snColor: "SN Color",
    drawingELU: "Drawing ELU",
    workELU: "Work ELU",
    cusPnELU: "Customer PN ELU",
    snElu: "SN ELU",
    mainBoard: "Main Board",
    multiPowerBoard: "Multi Power Board",
    topPcbBoard: "Top PCB Board",
    serialSsd: "Serial SSD",
    ssdType: "SSD Type",
    serialRam: "Serial RAM",
    ramType: "RAM Type"
  };

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token') || params.get('access_token') || '';
      setPublicToken(token ? token.trim() : '');
    } catch (err) {
      console.warn('Failed to parse public access token from URL', err);
      setPublicToken('');
    } finally {
      setTokenInitialized(true);
    }
  }, []);

  // Opened from Support Tools -> Start an RMA with ?rma=1: skip the hunt and
  // open the request form straight away, prefilled the same way the button does.
  const rmaAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (rmaAutoOpenedRef.current) return;
    if (!isSignedIn || isGpvRma) return;
    let wanted = false;
    try {
      wanted = new URLSearchParams(window.location.search).get('rma') === '1';
    } catch (err) {
      wanted = false;
    }
    if (!wanted) return;
    rmaAutoOpenedRef.current = true;
    const fullName = user?.username || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';
    setCustomerName(fullName);
    setContactEmail(user?.primaryEmailAddress?.emailAddress || '');
    setContactOrg(org?.name || '');
    setShowForm(true);
  }, [isSignedIn, isGpvRma, user, org]);

  useEffect(() => {
    let isActive = true;
    const loadDevice = async () => {
      if (!provider) {
        return;
      }
      try {
        setError(null);
        const payload = await provider.fetchDevice(id);
        if (!isActive) return;
        if (payload.device) {
          setDevice(payload.device);
        }
        if (Array.isArray(payload.rmas)) {
          setRmaList(payload.rmas.map(normalizeRma));
        } else if (!isSignedIn) {
          setRmaList([]);
        }
        if (payload.meta) {
          setPublicRmaMeta(prev => ({
            ...prev,
            ...payload.meta
          }));
        } else {
          setPublicRmaMeta({
            currentStatus: '',
            hasCustomerShipment: false,
            currentRmaId: null,
            highlightRmaId: null,
            accessScope: 'rma'
          });
        }
      } catch (err) {
        if (!isActive) return;
        if (!isSignedIn) {
          setDevice(null);
          setRmaList([]);
        }
        setError(err.message || 'Failed to load device.');
      }
    };
    loadDevice();
    return () => {
      isActive = false;
    };
  }, [id, provider, isSignedIn, formStatus, tokenInitialized]);

  useEffect(() => {
    if (device && device.productNumber) {
      const pdf = getProductPdf(device.productNumber);
      if (pdf) {
        setManualAvailable(true);
        setManualUrl(pdf);
      } else {
        setManualAvailable(false);
        setManualUrl(null);
      }
    }
  }, [device]);

  useEffect(() => {
    if (isGpvRma) {
      setShowForm(false);
      setShowImprovementForm(false);
    }
  }, [isGpvRma]);

  // Show org setup modal once per user — only if they haven't skipped or joined before
  useEffect(() => {
    if (!isCustomer || orgLoading) return;
    if (org !== null) return; // already in an org
    const userId = user?.id;
    if (!userId) return;
    const skippedKey = `ni_org_setup_skipped_${userId}`;
    if (localStorage.getItem(skippedKey)) return; // user has dismissed before
    setShowOrgSetup(true);
  }, [isCustomer, orgLoading, org, user]);

  // Load claim state for signed-in customers
  useEffect(() => {
    if (!isCustomer || !device) return;
    let active = true;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/org/device/${device._id}/claims`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok && active) {
          const data = await res.json();
          setClaimState(data);
        }
      } catch (_) {}
    })();
    return () => { active = false; };
  }, [isCustomer, device, getToken]);

  const handleClaimDevice = useCallback(async (country = '') => {
    if (!device) return;
    if (!org) { setShowOrgSetup(true); return; }
    setClaiming(true);
    setClaimError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/org/devices/${device._id}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: country || '' })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setClaimError(data.error || `Request failed (${res.status})`);
        return;
      }
      // Re-fetch claim state to update UI
      const token2 = await getToken();
      const r2 = await fetch(`${API_BASE}/org/device/${device._id}/claims`, {
        headers: { Authorization: `Bearer ${token2}` }
      });
      if (r2.ok) setClaimState(await r2.json());
    } catch (err) {
      setClaimError(err.message || 'Failed to claim device.');
    } finally {
      setClaiming(false);
    }
  }, [device, org, getToken]);

  useEffect(() => {
    if (isSignedIn) return;
    if (viewMode === 'archive') {
      setViewMode('current');
    }
    if (showProductData) {
      setShowProductData(false);
    }
  }, [isSignedIn, viewMode, showProductData]);

  useEffect(() => {
    if (device && device.serialNumber) {
      const prefix = `./${device.serialNumber}/fileset/`;
      const keys = dimmingContext.keys().filter(k => k.startsWith(prefix));
      if (keys.length > 0) {
        setDimmingFiles(keys.map(k => ({ name: k.replace(prefix, ''), url: dimmingContext(k) })));
        setDimmingAccessible(true);
      } else if (device.dimmingProfile) {
        // No per-serial fileset on record — fall back to the factory dimming
        // profile for the product family served by the backend (issue #574).
        setDimmingFiles([]);
        setDimmingAccessible(true);
      } else {
        setDimmingAccessible(false);
      }
    }
  }, [device]);

  const MAX_EXTRA_DEVICES = 9;

  const handleVerifyExtraDevice = async () => {
    const tag = extraTagInput.trim();
    if (!tag) return;
    if (extraDevices.length >= MAX_EXTRA_DEVICES) {
      setExtraTagError(`You can add up to ${MAX_EXTRA_DEVICES} extra devices here — contact support for larger returns.`);
      return;
    }
    const primarySerial = (deviceSerialNumber || '').toLowerCase();
    const already = [primarySerial, ...extraDevices.map(d => (d.serialNumber || '').toLowerCase())];
    if (already.includes(tag.toLowerCase())) {
      setExtraTagError('That device is already on this request.');
      return;
    }
    setExtraTagChecking(true);
    setExtraTagError('');
    try {
      const res = await fetch(`${API_BASE}/public/device/verify-tag/${encodeURIComponent(tag)}`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setExtraTagError(res.status === 429
          ? 'Too many lookups just now — please wait a moment and try again.'
          : 'Could not check that tag. Please try again.');
        return;
      }
      if (!data.found) {
        setExtraTagError('No device found with that service tag or serial number.');
        return;
      }
      const found = data.device || {};
      if (already.includes((found.serialNumber || '').toLowerCase())) {
        setExtraTagError('That device is already on this request.');
        return;
      }
      setExtraDevices(prev => [...prev, {
        serialNumber: found.serialNumber || tag,
        serviceTagNumber: found.serviceTagNumber || '',
        productNumber: found.productNumber || '',
        model: found.model || '',
        issueType: '',
        description: '',
        openRmaId: data.openRmaId || '',
        showDetail: false,
      }]);
      setExtraTagInput('');
    } catch (err) {
      setExtraTagError('Could not check that tag. Please try again.');
    } finally {
      setExtraTagChecking(false);
    }
  };

  const updateExtraDevice = (index, patch) =>
    setExtraDevices(prev => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const removeExtraDevice = (index) =>
    setExtraDevices(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Without this we cannot tell the requester anything: every status update
    // goes out to this address and nowhere else (issue #725). The input is
    // type="email" so the browser has already rejected malformed values --
    // blank is the only case that reaches here.
    const trimmedEmail = contactEmail.trim();
    if (!trimmedEmail) {
      setContactEmailError('Contact email is required — we send every case update to it.');
      return;
    }
    setContactEmailError('');
    const normalizedMount = expectedMountIncluded
      ? {
          type: expectedMountType,
          hmi: expectedMountHmi,
          qty: 1
        }
      : null;

    const normalizedAccessories = expectedAccessoriesIncluded
      ? {
          acPowerCableQty: sanitizeNonNegativeInt(expectedAccessories.acPowerCableQty),
          hdmiCableQty: sanitizeNonNegativeInt(expectedAccessories.hdmiCableQty),
          dpCableQty: sanitizeNonNegativeInt(expectedAccessories.dpCableQty),
          touchCableQty: sanitizeNonNegativeInt(expectedAccessories.touchCableQty)
        }
      : { acPowerCableQty: 0, hdmiCableQty: 0, dpCableQty: 0, touchCableQty: 0 };

    const expectedReturnPayload = {
      items: [],
      serialsPolicy: SERIAL_POLICY_DEFAULT,
      notes: '',
      shipmentType: expectedShipmentType,
      // Goods-in checks the shipment against this count, so it must include the
      // extra units the customer added (issue #712).
      deviceQuantity: 1 + extraDevices.length,
      mounted: Boolean(normalizedMount),
      mount: normalizedMount,
      accessories: normalizedAccessories
    };
    const formData = new FormData();
    // Legacy fields like returnReason and priority were removed in 2025.
    // Keep the payload minimal here so they are not reintroduced by mistake.
    formData.append('serialNumber', device.serialNumber);
    formData.append('customerName', customerName);
    formData.append('contactEmail', trimmedEmail);
    formData.append('contactOrg', contactOrg);
    formData.append('issueType', issueType);
    formData.append('description', description);
    if (customerExpectation) formData.append('customerExpectation', customerExpectation);
    formData.append('expectedReturn', JSON.stringify(expectedReturnPayload));
    if (preScreened === true) {
      formData.append('preScreened', 'true');
      formData.append('screeningNotes', screeningNotes);
    }
    if (extraDevices.length) {
      // Each extra unit inherits the case description unless it carries its own.
      formData.append('devices', JSON.stringify(extraDevices.map(d => ({
        serialNumber: d.serialNumber,
        issueType: d.issueType || issueType,
        description: d.description || description,
      }))));
    }
    Array.from(rmaImages).forEach(img => formData.append('images', img));

    try {
      const res = await fetch(`${API_BASE}/rma`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        setFormStatus('RMA submitted successfully.');
        setCustomerName('');
        setContactEmail('');
        setContactEmailError('');
        setContactOrg('');
        setIssueType('hardware');
        setDescription('');
        setRmaImages([]);
        setExtraDevices([]);
        setExtraTagInput('');
        setExtraTagError('');
        setShowExtraDevices(false);
        setSubmittedDeviceCount(1 + extraDevices.length);
        setPreScreened(null);
        setScreeningNotes('');
        setPartialShipmentWarning(null);
        resetExpectedReturn();
        setShowForm(false);
      } else {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'CONTACT_EMAIL_REQUIRED' || data.error === 'INVALID_EMAIL') {
          setContactEmailError('That email address was not accepted. Please check it and try again.');
          setFormStatus('');
        } else {
          setFormStatus('Failed to submit RMA.');
        }
      }
    } catch (err) {
      setFormStatus('Error submitting RMA.');
    }
  };

  const handleImprovementSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      serialNumber: device.serialNumber,
      customerName: impName,
      contactEmail: impEmail,
      category: impCategory,
      description: impDescription,
    };
    try {
      const res = await fetch(`${API_BASE}/improvement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setImpStatus('Improvement submitted successfully.');
        setImpName('');
        setImpEmail('');
        setImpCategory('ui');
        setImpDescription('');
        setShowImprovementForm(false);
      } else {
        setImpStatus('Failed to submit improvement.');
      }
    } catch (err) {
      setImpStatus('Error submitting improvement.');
    }
  };

  const handleModalClose = () => setActiveRma(null);

  const handleUpdate = (updated, opts = {}) => {
    if (!updated || typeof updated !== 'object') {
      setActiveRma(null);
      return;
    }
    if (updated.archived) {
      setRmaList(prev => prev.filter(r => r._id !== activeRma._id));
      if (archiveRefetchRef.current) {
        archiveRefetchRef.current();
      }
      if (!opts?.keepOpen) {
        setActiveRma(null);
      }
    } else {
      const normalized = normalizeRma(updated);
      setRmaList(prev => prev.map(r => (r._id === normalized._id ? normalized : r)));
      if (opts?.keepOpen) {
        // Stay open and give the modal fresh data so it reflects the saved state
        setActiveRma(normalized);
      } else {
        setActiveRma(null);
      }
    }
  };

  const getFileExtension = (name) => {
    if (!name || typeof name !== 'string') return '';
    const dotIndex = name.lastIndexOf('.');
    return dotIndex >= 0 ? name.slice(dotIndex).toLowerCase() : '';
  };

  const isFileAllowed = (file) => {
    if (!file) return false;
    const extension = getFileExtension(file.name);
    if (HEIC_TYPES.has(file.type) || HEIC_EXTENSIONS.has(extension)) return 'heic';
    return (
      CHALLENGE_ALLOWED_TYPES.includes(file.type) ||
      CHALLENGE_ALLOWED_EXTENSIONS.includes(extension)
    );
  };

  const formatFileSize = (size) => {
    if (!Number.isFinite(size) || size <= 0) return '';
    if (size >= 1048576) {
      return `${(size / 1048576).toFixed(1)} MB`;
    }
    return `${Math.ceil(size / 1024)} KB`;
  };

  const openChallengeModal = (rma, opts) => {
    if (opts?.updatedRma) {
      const normalized = normalizeRma(opts.updatedRma);
      setRmaList(prev => prev.map(r => r._id === normalized._id ? normalized : r));
      setActiveRma(normalized);
      return;
    }
    setChallengeTarget(rma);
    setChallengeMessage('');
    setChallengeEmail('');
    setChallengeFiles([]);
    setChallengeErrors({});
    setChallengeError('');
  };

  const closeChallengeModal = () => {
    setChallengeTarget(null);
    setChallengeMessage('');
    setChallengeEmail('');
    setChallengeFiles([]);
    setChallengeErrors({});
    setChallengeError('');
  };

  const handleChallengeFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    setChallengeFiles(prev => {
      const nextFiles = [...prev];
      const errors = [];
      for (const file of selectedFiles) {
        if (nextFiles.length >= CHALLENGE_MAX_FILES) {
          errors.push(`You can upload up to ${CHALLENGE_MAX_FILES} files.`);
          break;
        }
        if (file.size > CHALLENGE_MAX_FILE_SIZE) {
          errors.push(`${file.name} exceeds the 10 MB limit.`);
          continue;
        }
        const allowed = isFileAllowed(file);
        if (allowed === 'heic') {
          errors.push(`${file.name}: HEIC photos aren't supported — open in Photos and export as JPG first.`);
          continue;
        }
        if (!allowed) {
          errors.push(`${file.name}: only JPG, PNG, WebP, GIF, or PDF files are allowed.`);
          continue;
        }
        nextFiles.push(file);
      }
      setChallengeErrors(prevErrors => {
        const updated = { ...prevErrors };
        if (errors.length) {
          updated.attachments = errors.join(' ');
        } else {
          delete updated.attachments;
        }
        return updated;
      });
      return nextFiles;
    });

    if (event.target) {
      event.target.value = '';
    }
  };

  const removeChallengeFile = (index) => {
    setChallengeFiles(prev => prev.filter((_, idx) => idx !== index));
    setChallengeErrors(prev => {
      if (!prev.attachments) return prev;
      const next = { ...prev };
      delete next.attachments;
      return next;
    });
  };

  const validateChallengeInputs = () => {
    const trimmedMessage = challengeMessage.trim();
    const trimmedEmail = challengeEmail.trim();
    const nextErrors = {};

    if (trimmedMessage.length < 10) {
      nextErrors.message = 'Reason must be at least 10 characters.';
    } else if (trimmedMessage.length > 2000) {
      nextErrors.message = 'Reason must be 2000 characters or fewer.';
    }

    if (trimmedEmail && !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (challengeFiles.length > CHALLENGE_MAX_FILES) {
      nextErrors.attachments = `You can upload up to ${CHALLENGE_MAX_FILES} files.`;
    } else {
      for (const file of challengeFiles) {
        if (file.size > CHALLENGE_MAX_FILE_SIZE) {
          nextErrors.attachments = 'Each attachment must be 10 MB or less.';
          break;
        }
        const allowed = isFileAllowed(file);
        if (allowed === 'heic') {
          nextErrors.attachments = 'HEIC photos aren\'t supported — export as JPG from Photos first.';
          break;
        }
        if (!allowed) {
          nextErrors.attachments = 'Only JPG, PNG, WebP, GIF, or PDF files are allowed.';
          break;
        }
      }
    }

    setChallengeErrors(nextErrors);
    return nextErrors;
  };

  const handleSubmitChallenge = async (event) => {
    event.preventDefault();
    if (!challengeTarget) return;

    const errors = validateChallengeInputs();
    if (Object.keys(errors).length > 0) {
      return;
    }

    setChallengeError('');
    setChallengeSubmittingId(challengeTarget._id);

    try {
      const formData = new FormData();
      formData.append('message', challengeMessage.trim());
      const trimmedEmail = challengeEmail.trim();
      if (trimmedEmail) {
        formData.append('email', trimmedEmail);
      }
      challengeFiles.forEach(file => formData.append('attachments', file));

      let endpoint = `${API_BASE}/rma/${challengeTarget._id}/challenge`;

      let token = null;
      try {
        token = await getToken();
      } catch (tokenErr) {
        if (tokenErr && process.env.NODE_ENV !== 'production') {
          console.warn('Failed to fetch auth token for challenge submission', tokenErr);
        }
      }

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!res.ok) {
        let message = 'Failed to submit challenge.';
        try {
          const payload = await res.json();
          if (payload?.message) message = payload.message;
          else if (payload?.error) message = payload.error;
        } catch (_) {
          // ignore secondary parse errors
        }
        throw new Error(message);
      }

      const payload = await res.json();
      if (payload?.rma) {
        const normalized = normalizeRma(payload.rma);
        setRmaList(prev => prev.map(r => (r._id === normalized._id ? normalized : r)));
      } else if (device?.serialNumber) {
        fetch(`${API_BASE}/rma/${device.serialNumber}`)
          .then(res => res.json())
          .then(data => setRmaList(Array.isArray(data) ? data.map(normalizeRma) : []))
          .catch(err => console.error('Failed to refresh RMAs after challenge', err));
      }

      closeChallengeModal();
    } catch (err) {
      console.error('Failed to submit challenge', err);
      setChallengeError(err.message || 'Failed to submit challenge.');
    } finally {
      setChallengeSubmittingId(null);
    }
  };

  const handleFollowupSaved = (updatedFollowup) => {
    setDevice(prev => (prev ? { ...prev, gpvRmaFollowup: updatedFollowup } : prev));
  };

  const handleCustomerDecision = async (rma, decision) => {
    if (!rma || !rma._id || !decision) {
      return;
    }

    const rmaId = rma._id;

    setCustomerDecisionState(prev => ({
      ...prev,
      [rmaId]: {
        loading: true,
        error: ''
      }
    }));

    try {
      let endpoint = `${API_BASE}/rma/${rmaId}/customer-decision`;

      let token = null;
      try {
        token = await getToken();
      } catch (tokenErr) {
        if (tokenErr && process.env.NODE_ENV !== 'production') {
          console.warn('Failed to fetch auth token for customer decision', tokenErr);
        }
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ decision })
      });

      if (!res.ok) {
        let message = 'Failed to submit decision.';
        try {
          const payload = await res.json();
          if (payload?.message) message = payload.message;
          else if (payload?.error) message = payload.error;
        } catch (_) {
          // ignore json parse errors
        }
        throw new Error(message);
      }

      const payload = await res.json();
      if (payload && payload._id) {
        const normalized = normalizeRma(payload);
        setRmaList(prev => prev.map(r => (r._id === normalized._id ? normalized : r)));
        setActiveRma(normalized);
      } else if (device?.serialNumber) {
        fetch(`${API_BASE}/rma/${device.serialNumber}`)
          .then(res => res.json())
          .then(data => setRmaList(Array.isArray(data) ? data.map(normalizeRma) : []))
          .catch(err => console.error('Failed to refresh RMAs after decision', err));
      }

      setCustomerDecisionState(prev => ({
        ...prev,
        [rmaId]: {
          loading: false,
          error: ''
        }
      }));
    } catch (err) {
      console.error('Failed to submit customer decision', err);
      setCustomerDecisionState(prev => ({
        ...prev,
        [rmaId]: {
          loading: false,
          error: err.message || 'Failed to submit decision.'
        }
      }));
    }
  };

  const handleDeclinePath = async (rma, choice, payload = {}) => {
    if (!rma || !rma._id || !choice) {
      return;
    }

    const rmaId = rma._id;
    setDeclinePathState(prev => ({
      ...prev,
      [rmaId]: {
        loading: true,
        error: ''
      }
    }));

    try {
      let endpointSlug = '';
      if (choice === 'return_as_is') {
        endpointSlug = 'return';
      } else if (choice === 'retain_and_close') {
        endpointSlug = 'close';
      } else {
        throw new Error('Unsupported decline path.');
      }

      let endpoint = `${API_BASE}/rma/${rmaId}/decline-path/${endpointSlug}`;

      let token = null;
      try {
        token = await getToken();
      } catch (tokenErr) {
        if (tokenErr && process.env.NODE_ENV !== 'production') {
          console.warn('Failed to fetch auth token for decline path', tokenErr);
        }
      }

      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const bodyPayload = { ...payload };
      if (choice === 'return_as_is') {
        bodyPayload.shippingCostAck = true;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyPayload)
      });

      if (!res.ok) {
        let message = 'Failed to submit choice.';
        try {
          const response = await res.json();
          if (response?.message) message = response.message;
          else if (response?.error) message = response.error;
        } catch (_) {
          // ignore json parse errors
        }
        throw new Error(message);
      }

      const updatedPayload = await res.json();
      if (updatedPayload && updatedPayload._id) {
        const normalized = normalizeRma(updatedPayload);
        setRmaList(prev => prev.map(r => (r._id === normalized._id ? normalized : r)));
        setActiveRma(normalized);
      } else if (device?.serialNumber) {
        fetch(`${API_BASE}/rma/${device.serialNumber}`)
          .then(resFetch => resFetch.json())
          .then(data => setRmaList(Array.isArray(data) ? data.map(normalizeRma) : []))
          .catch(err => console.error('Failed to refresh RMAs after decline choice', err));
      }

      setDeclinePathState(prev => ({
        ...prev,
        [rmaId]: {
          loading: false,
          error: ''
        }
      }));
    } catch (err) {
      console.error('Failed to submit decline path', err);
      setDeclinePathState(prev => ({
        ...prev,
        [rmaId]: {
          loading: false,
          error: err.message || 'Failed to submit choice.'
        }
      }));
    }
  };

  const handleCustomerShipmentSaved = updated => {
    if (!updated || typeof updated !== 'object') {
      return;
    }
    const normalized = normalizeRma(updated);
    setRmaList(prev => {
      const exists = prev.some(entry => entry._id === normalized._id);
      if (!exists) {
        return [...prev, normalized];
      }
      return prev.map(entry => (entry._id === normalized._id ? normalized : entry));
    });
    setPublicRmaMeta(prev => ({
      ...prev,
      hasCustomerShipment: Boolean(
        normalized.customerTracking?.trackingNumber || normalized.customerShipment?.trackingNumber
      ),
      currentRmaId: normalized._id || prev.currentRmaId,
      highlightRmaId: normalized.rmaId || prev.highlightRmaId,
      currentStatus: normalized.status || prev.currentStatus
    }));
    setActiveRma(normalized);
  };

  const sortedRmas = useMemo(() => {
    if (!Array.isArray(rmaList)) return [];
    return [...rmaList].sort((a, b) => {
      const aTime = a?.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const bTime = b?.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [rmaList]);

  const latestRmaStage = sortedRmas.length > 0 ? (sortedRmas[0]?.status || '') : '';
  const isDeviceDecommissioned = sortedRmas.some(item => item?.ui?.decommissioned);
  const decommissionedRma = sortedRmas.find(item => item?.ui?.decommissioned);
  const refurbDecision = decommissionedRma?.refurbishment?.decision || null;
  const refurbStatus = decommissionedRma?.refurbishment?.status || null;
  const refurbNewSerial = decommissionedRma?.refurbishment?.newSerialNumber || null;
  const refurbNewDeviceId = decommissionedRma?.refurbishment?.newDeviceId || null;
  const deviceShellClass = isDeviceDecommissioned
    ? isServiceUser
      ? refurbStatus === 'recommissioned' ? 'device-shell--refurbished'
        : refurbDecision === 'scrap' || refurbStatus === 'scrapped' ? 'device-shell--scrapped'
        : 'device-shell--decommissioned'
      : 'device-shell--decommissioned'
    : '';
  const canOpenRmaModal = isServiceUser || isGuestView || isCustomer;

  const warrantyTooltip = 'Warranty coverage is calculated from the scan date and the North Invent service policy. Click to view details.';

  const modalStatus = activeRma ? normalizeStatusKey(activeRma.status) : null;
  const modalTable = isServiceUser ? SERVICE_MODAL_COMPONENTS : CUSTOMER_MODAL_COMPONENTS;
  const ModalComponent = modalStatus ? modalTable[modalStatus] : null;
  const modalExtraProps = {};
  if (modalStatus === 'assigned') {
    if (!isSignedIn && provider?.mode === 'public' && activeRma) {
      modalExtraProps.trackingActions = {
        canSubmit: true,
        submit: submission => provider.submitTracking(activeRma, submission)
      };
    } else if (isCustomer && activeRma) {
      modalExtraProps.trackingActions = {
        canSubmit: true,
        submit: async (submission) => {
          const token = await getToken();
          const res = await fetch(`${API_BASE}/rma/${activeRma._id}/customer-shipment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              trackingNumber: submission.trackingNumber,
              carrier: submission.carrier,
              comment: submission.comment
            })
          });
          if (!res.ok) {
            let message = 'Failed to submit tracking.';
            try {
              const payload = await res.json();
              if (payload?.error) message = payload.error;
              else if (payload?.message) message = payload.message;
            } catch (_) {}
            throw new Error(message);
          }
          return await res.json();
        }
      };
    }
    modalExtraProps.onShipmentSaved = handleCustomerShipmentSaved;
  }
  if (modalStatus === 'repairing' && activeRma) {
    modalExtraProps.onCustomerDecision = handleCustomerDecision;
    modalExtraProps.customerDecisionState = customerDecisionState[activeRma._id];
    modalExtraProps.onChallenge = openChallengeModal;
    modalExtraProps.challengeSubmittingId = challengeSubmittingId;
    modalExtraProps.onDeclinePath = handleDeclinePath;
    modalExtraProps.declinePathState = declinePathState[activeRma._id];
  }
  const isChallengeSubmitting = challengeTarget ? challengeSubmittingId === challengeTarget._id : false;

  if (error) return <div style={{ color: 'red', padding: '1rem' }}>Error: {error}</div>;
  if (!device && isGuestView)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
        <img src="/north-invent-logo.svg" alt="North Invent Logo" style={{ height: '48px', opacity: 0.85, marginBottom: '0.5rem' }} />
        <h2 style={{ color: colors.textStrong, margin: 0 }}>Device Info</h2>
        <p style={{ color: colors.textMuted, margin: 0, maxWidth: '360px' }}>
          Sign in to view device details, warranty status, and RMA history.
        </p>
        <SignInButton mode="modal">
          <button className="ni-button ni-button--primary">Sign in</button>
        </SignInButton>
      </div>
    );

  if (!device)
    return (
      <div style={{ color: colors.textStrong, padding: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LoadingSpinner />
      </div>
    );

  const warrantyExtraMonths = (isCustomer && claimState?.claimedByMyOrg) ? CLAIM_WARRANTY_BONUS_MONTHS : 0;
  const orgAgreementMonths = isSignedIn ? getOrgWarrantyExtra(org?.name) : 0;
  // Family-scoped base warranty period (months). Falls back to the 36-month
  // default when this product's family has no warranty rule set.
  const familyWarrantyMonths = Number(device.familyProfile?.warranty?.months) > 0
    ? Number(device.familyProfile.warranty.months)
    : 36;
  // Additive company-specific warranty (e.g. SAAB) resolved from the part number.
  const companyWarrantyMonths = Number(device.companyProfile?.companyWarrantyExtraMonths) > 0
    ? Number(device.companyProfile.companyWarrantyExtraMonths)
    : 0;
  const warrantyStatus = getWarrantyStatus(device.scandate, undefined, warrantyExtraMonths, orgAgreementMonths, familyWarrantyMonths, companyWarrantyMonths);
  const warrantyColor = getWarrantyColor(warrantyStatus);
  const warrantyText =
    warrantyStatus === 'within'
      ? 'Within warranty'
      : warrantyStatus === 'warning'
      ? 'Warranty expires soon'
      : warrantyStatus === 'outside'
      ? 'Outside of warranty'
      : 'Warranty unknown';

  const operationStats = getOperationStats(device);
  const dataQualityScore = computeDataQualityScore({
    ...device,
    operatingHours: operationStats?.operatingHours,
    warrantyStatus,
    manualAvailable,
  });

  return (
    <div style={deviceStyles.page}>
      <div
        className={`device-shell${deviceShellClass ? ' ' + deviceShellClass : ''}`}
        style={deviceStyles.shell}
      >
        <div style={deviceStyles.header}>
          <div style={deviceStyles.headerRow}>
            <div style={{ display: 'grid', gap: '0.35rem' }}>
              <div className="ni-eyebrow">North Invent</div>
              <h1 style={deviceStyles.title}>
                Device Info
                {device.productFamily === 'test' && (
                  <span style={{
                    display: 'inline-block', marginLeft: '0.6rem', padding: '0.1rem 0.55rem',
                    borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                    verticalAlign: 'middle', background: 'rgba(234,179,8,0.18)',
                    border: '1px solid rgba(234,179,8,0.5)', color: '#eab308',
                  }}>
                    TEST INSTANCE
                  </span>
                )}
              </h1>
              <p style={deviceStyles.subtitle}>
                Service tag {device.serviceTagNumber ?? 'N/A'} · Product {device.productNumber ?? 'N/A'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
                <img
                  src='/wave-configurator-app/inspiration/Horizontal logo SVG/SVG/North-Invent-neg-green-Long.svg'
                  alt="North Invent Logo"
                  style={{ height: isGuestView ? '44px' : '68px', width: 'auto', opacity: 0.95 }}
                />
              </Link>
            {isSignedIn && !isCustomer ? (
              dataQualityScore == null ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: colors.textStrong }}>
                  <LoadingSpinner size={16} />
                  <span>Calculating data quality...</span>
                </div>
              ) : (
                <span
                  title="Computed from required fields present vs. missing (N/A)"
                  style={deviceStyles.qualityBadge}
                >
                  <span>Data Quality:</span>
                  <span>{dataQualityScore} / 100</span>
                </span>
              )
            ) : (
              <SignInButton mode="modal">
                <button className="ni-button ni-button--primary" style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}>
                  Sign in
                </button>
              </SignInButton>
            )}
            {isCustomer && (
              <div style={{ position: 'relative' }}>
                <button
                  aria-label="Menu"
                  onClick={() => setShowHamburger(v => !v)}
                  style={{
                    background: 'none', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px', padding: '0.4rem 0.55rem', cursor: 'pointer',
                    color: colors.textStrong, fontSize: '1.1rem', lineHeight: 1
                  }}
                >
                  ☰
                </button>
                {showHamburger && (
                  <div
                    style={{
                      position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
                      background: '#111b16', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '10px', padding: '0.5rem', minWidth: '220px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 200
                    }}
                    onMouseLeave={() => setShowHamburger(false)}
                  >
                    <button
                      onClick={() => { setShowHamburger(false); navigate('/org/devices'); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        background: 'none', border: 'none', color: colors.textStrong,
                        padding: '0.6rem 0.75rem', borderRadius: '6px', cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      📋 Your company's devices
                    </button>
                    {org && (
                      <div style={{ padding: '0.4rem 0.75rem', color: colors.textMuted, fontSize: '0.78rem', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: '0.25rem' }}>
                        {org.name}{org.verified ? ' ✓' : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
        <div style={deviceStyles.content}>
          <div style={deviceStyles.imageWrap}>
            <ProductThumb
                          productNumber={device.productNumber}
                          style={deviceStyles.image}
                        />
            {!isGuestView && <div style={deviceStyles.viewModeWrap}>
              <button
                onClick={() => setViewMode('current')}
                style={{
                  ...deviceStyles.viewModeButton,
                  ...(viewMode === 'current' ? deviceStyles.viewModeButtonActive : {})
                }}
              >
                Current
              </button>
              {device.productProfile && (
                <button
                  onClick={() => setViewMode('metadata')}
                  style={{
                    ...deviceStyles.viewModeButton,
                    ...(viewMode === 'metadata' ? deviceStyles.viewModeButtonActive : {})
                  }}
                >
                  Metadata
                </button>
              )}
              {isSignedIn && !isGpvRma && (
                <button
                  onClick={() => setViewMode('archive')}
                  style={{
                    ...deviceStyles.viewModeButton,
                    ...(viewMode === 'archive' ? deviceStyles.viewModeButtonActive : {})
                  }}
                >
                  Archive
                </button>
              )}
              {isSignedIn && !isCustomer && (
                <button
                  onClick={() => setViewMode('stats')}
                  style={{
                    ...deviceStyles.viewModeButton,
                    ...(viewMode === 'stats' ? deviceStyles.viewModeButtonActive : {})
                  }}
                >
                  Stats
                </button>
              )}
            </div>}
          </div>
          <div style={deviceStyles.details}>
            <div style={deviceStyles.detailBlock}>
              <span style={deviceStyles.detailLabel}>Service Tag Number</span>
              <span style={deviceStyles.detailValue}>{device.serviceTagNumber ?? 'N/A'}</span>
              {isServiceUser && device.manufacturerProfile?.manufacturer && (
                <span style={{ fontSize: '0.78rem', color: colors.textMuted }}>
                  Built by {device.manufacturerProfile.manufacturer} ({device.manufacturerProfile.manufacturerLetter})
                </span>
              )}
            </div>
            <div style={deviceStyles.detailBlock}>
              <span style={deviceStyles.detailLabel}>Product Number</span>
              <span style={deviceStyles.detailValue}>{device.productNumber ?? 'N/A'}</span>
            </div>
            {isServiceUser && device.customer && (
              <div style={deviceStyles.detailBlock}>
                <span style={deviceStyles.detailLabel}>Customer</span>
                <span style={deviceStyles.detailValue}>{device.customer}</span>
                {device.shippedTo && (
                  <span style={{ fontSize: '0.78rem', color: colors.textMuted }}>Shipped to: {device.shippedTo}</span>
                )}
              </div>
            )}
            {manualAvailable && !isGpvRma && (
              <p style={{ margin: 0 }}>
                <a href={manualUrl} target="_blank" rel="noopener noreferrer" style={{ color: colors.accent }}>
                  Product data available.
                </a>
              </p>
            )}
            {!manualAvailable && !isGuestView && !isCustomer && !isGpvRma && (
              <p style={{ margin: 0, color: colors.textMuted }}>Product data not available. Please contact North Invent.</p>
            )}
            <p
              onClick={() => setShowWarrantyPopup(true)}
              style={{ color: warrantyColor, fontWeight: 'bold', textDecoration: 'underline', margin: 0, cursor: 'pointer' }}
            >
              {warrantyText}
            </p>
            {isSignedIn && !isGpvRma && (
              <div>
                {isAdmin ? (
                  <select
                    value={device.assigned || 'in field'}
                    onChange={async (e) => {
                      const newVal = e.target.value;
                      const res = await fetch(`${API_BASE}/device/${device._id}/assigned`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ assigned: newVal })
                      });
                      if (res.ok) {
                        const updated = await res.json();
                        setDevice(updated);
                      }
                    }}
                    className="ni-select"
                    style={{ maxWidth: '260px' }}
                  >
                    {ASSIGNED_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : isCustomer ? (
                  (() => {
                    const allOrgs = [
                      ...(claimState?.claimedByMyOrg && org?.name ? [org.name] : []),
                      ...(claimState?.otherClaimants?.map(c => c.name) || [])
                    ];
                    return allOrgs.length > 0 ? (
                      <div style={deviceStyles.detailBlock}>
                        <span style={deviceStyles.detailLabel}>Claimed by</span>
                        <span style={deviceStyles.detailValue}>{allOrgs.join(', ')}</span>
                      </div>
                    ) : null;
                  })()
                ) : (
                  <div style={deviceStyles.detailBlock}>
                    <span style={deviceStyles.detailLabel}>Assigned</span>
                    <span style={deviceStyles.detailValue}>{device.assigned ?? 'N/A'}</span>
                  </div>
                )}
              </div>
            )}
            <div style={deviceStyles.actionRow}>
              {isSignedIn && (
                <button
                  className="ni-button ni-button--primary"
                  type="button"
                  onClick={() => setShowProductData(true)}
                  onFocus={() => setIsProductButtonFocused(true)}
                  onBlur={() => setIsProductButtonFocused(false)}
                  style={{
                    minWidth: '180px',
                    boxShadow: isProductButtonFocused
                      ? `0 0 0 3px rgba(11,15,13,0.92), 0 0 0 6px rgba(70, 187, 137, 0.35)`
                      : undefined,
                    outline: 'none',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                    transform: isProductButtonFocused ? 'translateY(-1px)' : 'none'
                  }}
                >
                  Show Product Data
                </button>
              )}
              {isSignedIn && !isGpvRma && (
                <button
                  className="ni-button ni-button--ghost"
                  onClick={() => {
                    const fullName = user?.username || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';
                    setCustomerName(fullName);
                    setContactEmail(user?.primaryEmailAddress?.emailAddress || '');
                    setContactOrg(org?.name || '');
                    setShowForm(true);
                  }}
                >
                  Start RMA
                </button>
              )}
              {isGuestView && (
                <button
                  className="ni-button ni-button--ghost"
                  onClick={() => setShowGuestRmaPrompt(true)}
                >
                  Start Repair Request
                </button>
              )}
              {isCustomer && claimState && (
                <div style={{ width: '100%', marginTop: '0.25rem' }}>
                  {claimState.claimedByMyOrg ? (
                    <p style={{ color: '#46bb89', margin: 0, fontSize: '0.85rem' }}>
                      ✓ Claimed by your company{org?.verified ? ' (Verified)' : ''} — {CLAIM_WARRANTY_LABEL} warranty active
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      <style>{`
                        @keyframes claimPulse {
                          0%, 100% { box-shadow: 0 0 0 0 rgba(70,187,137,0.45); }
                          50%       { box-shadow: 0 0 0 6px rgba(70,187,137,0); }
                        }
                      `}</style>
                      <div style={{
                        border: '1px solid rgba(70,187,137,0.3)',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        background: 'rgba(70,187,137,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        <p style={{ color: colors.textStrong, margin: 0, fontSize: '0.85rem', fontWeight: 600 }}>
                          🔒 Unlock an extra {CLAIM_WARRANTY_BONUS_MONTHS % 12 === 0 ? `${CLAIM_WARRANTY_BONUS_MONTHS / 12} year` : `${CLAIM_WARRANTY_BONUS_MONTHS} month`} of warranty
                        </p>
                        <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.82rem', lineHeight: 1.55 }}>
                          {claimState.otherClaimants?.length > 0
                            ? `Also claimed by ${claimState.otherClaimants.map(c => c.name).join(', ')}. The ${CLAIM_WARRANTY_LABEL} warranty extension still applies to your company.`
                            : `Claim this device to your company and extend the warranty by ${CLAIM_WARRANTY_BONUS_MONTHS % 12 === 0 ? `${CLAIM_WARRANTY_BONUS_MONTHS / 12} year` : `${CLAIM_WARRANTY_BONUS_MONTHS} month`}.`}
                        </p>
                        <button
                          className="ni-button ni-button--primary"
                          style={{
                            alignSelf: 'flex-start',
                            fontSize: '0.85rem',
                            padding: '0.4rem 1.1rem',
                            animation: 'claimPulse 2.4s ease-in-out infinite'
                          }}
                          onClick={() => {
                            if (!org) { setShowOrgSetup(true); return; }
                            setClaimCountry('');
                            setShowClaimLocationModal(true);
                          }}
                          disabled={claiming}
                        >
                          {claiming ? 'Claiming…' : `Claim device (${CLAIM_WARRANTY_LABEL} warranty)`}
                        </button>
                      </div>
                      {claimError && (
                        <p style={{ color: '#f87171', margin: 0, fontSize: '0.82rem' }}>{claimError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {dimmingAccessible !== null && (isSignedIn || dimmingAccessible) && (!isCustomer || dimmingAccessible) && (
                <p
                  style={{
                    width: '100%',
                    cursor: dimmingAccessible ? 'pointer' : 'default',
                    textDecoration: dimmingAccessible ? 'underline' : 'none',
                    color: colors.textMuted,
                    margin: 0
                  }}
                  onClick={() => dimmingAccessible && setShowDimmingModal(true)}
                >
                  {dimmingAccessible ? 'Dimming data accessible' : 'Dimming data not accessible.'}
                </p>
              )}
            </div>
          </div>
        </div>
        {!isGuestView && <NorthstarHistory key={device?._id} serviceTag={device?.serviceTagNumber} />}
        {!isGuestView && <div style={deviceStyles.section}>
          {showMeta ? (
            <ProductMetadataView profile={device.productProfile} productNumber={device.productNumber} />
          ) : showStats ? (
            operationStats ? (
              <div className="ni-grid">
                <p>
                  <strong style={{ color: colors.textStrong }}>Total Operating Time</strong>: {operationStats.operatingHours !== null ? operationStats.operatingHours.toFixed(0) : 'N/A'} hours
                </p>
                <p>
                  <strong style={{ color: colors.textStrong }}>Failure Count</strong>: {operationStats.failureCount}
                </p>
              </div>
            ) : (
              <p style={{ color: colors.textMuted, margin: 0 }}>Operation stats unavailable.</p>
            )
          ) : showArchived ? (
            <DeviceArchivePanel
              deviceId={device?._id}
              serialNumber={device?.serialNumber}
              isServiceUser={isServiceUser}
              isVisible={showArchived}
              onRefetchRef={(fn) => {
                archiveRefetchRef.current = fn;
              }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h2 style={deviceStyles.sectionTitle}>Repair Intake & History</h2>
              {isServiceUser && isDeviceDecommissioned && (
                <div style={{
                  borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '0.25rem',
                  border: refurbStatus === 'recommissioned'
                    ? '1px solid rgba(70,187,137,0.3)' : refurbDecision === 'scrap' || refurbStatus === 'scrapped'
                    ? '1px solid rgba(248,113,113,0.3)' : '1px solid rgba(251,146,60,0.3)',
                  backgroundColor: refurbStatus === 'recommissioned'
                    ? 'rgba(70,187,137,0.07)' : refurbDecision === 'scrap' || refurbStatus === 'scrapped'
                    ? 'rgba(248,113,113,0.07)' : 'rgba(251,146,60,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap'
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color:
                      refurbStatus === 'recommissioned' ? '#46bb89'
                      : refurbDecision === 'scrap' || refurbStatus === 'scrapped' ? '#f87171'
                      : '#fb923c'
                    }}>
                      {refurbStatus === 'recommissioned' ? 'Refurbished & recommissioned'
                        : refurbDecision === 'scrap' || refurbStatus === 'scrapped' ? 'Unit scrapped'
                        : refurbDecision === 'repair' ? 'Refurbishment in progress'
                        : 'Unit decommissioned — awaiting disposition'}
                    </div>
                    {refurbNewSerial && (
                      <div style={{ fontSize: '0.82rem', color: '#aaa', marginTop: '0.2rem' }}>
                        New service tag: <strong style={{ color: '#46bb89' }}>{refurbNewSerial}</strong>
                      </div>
                    )}
                    {decommissionedRma?.refurbishment?.refurbId && (
                      <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.1rem' }}>
                        Refurb ID: {decommissionedRma.refurbishment.refurbId}
                      </div>
                    )}
                  </div>
                  {refurbNewSerial && (
                    <a
                      href={`/device/${refurbNewDeviceId || refurbNewSerial}`}
                      style={{
                        padding: '0.45rem 1rem', borderRadius: '999px', border: '1px solid rgba(70,187,137,0.4)',
                        backgroundColor: 'rgba(70,187,137,0.1)', color: '#46bb89',
                        fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none', whiteSpace: 'nowrap'
                      }}
                    >
                      View refurbished device →
                    </a>
                  )}
                </div>
              )}
                <button
                  type="button"
                  onClick={() => setShowWarrantyPopup(true)}
                  title={warrantyTooltip}
                  style={{
                    backgroundColor: warrantyColor,
                    color: warrantyColor === colors.statusYellow ? '#2b2310' : colors.textStrong,
                    border: 'none',
                    borderRadius: '999px',
                    padding: '0.35rem 0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <span>Warranty:</span>
                  <span>{warrantyText}</span>
                </button>
              </div>
              {isGpvRma && (
                <GpvRmaFollowupPanel
                  deviceId={device._id}
                  followup={device.gpvRmaFollowup}
                  serialNumber={device.serialNumber}
                  latestStage={latestRmaStage}
                  apiBase={API_BASE}
                  getToken={getToken}
                  onFollowupUpdated={handleFollowupSaved}
                />
              )}
              {sortedRmas.length === 0 ? (
                <div className="ni-panel ni-panel--dense">
                  <p style={{ color: colors.textMuted, margin: 0 }}>No active RMAs.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {isSignedIn ? (
                    sortedRmas.map(rma => (
                      <RmaTimelineCard
                        key={rma._id}
                        rma={rma}
                        onSelect={canOpenRmaModal ? () => setActiveRma(rma) : undefined}
                        showManage={isServiceUser}
                        onCustomerDecision={handleCustomerDecision}
                        customerDecisionState={customerDecisionState[rma._id]}
                        onDeclinePath={handleDeclinePath}
                        declinePathState={declinePathState[rma._id]}
                      />
                    ))
                  ) : (
                    sortedRmas.map(rma => (
                      <RmaTimelineCard
                        key={rma._id || rma.rmaId}
                        rma={rma}
                        onSelect={canOpenRmaModal ? () => setActiveRma(rma) : undefined}
                        showManage={false}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>}
      </div>

      {isGuestView && device?.serialNumber && publicRmaMeta.hasActiveRma && (
        <div style={deviceStyles.section}>
          <GuestRmaLookup
            serialNumber={device.serialNumber}
            rmas={sortedRmas}
            onRmasFound={found => setRmaList(found.map(normalizeRma))}
            onOpenRma={rma => setActiveRma(rma)}
          />
        </div>
      )}

      {isGuestView && <GuestDevicePopup />}

      {showOrgSetup && isCustomer && (
        <OrgSetupModal
          onJoin={async (name) => {
            const joined = await joinOrg(name);
            // Save org reference to Clerk unsafeMetadata so we never ask again
            try {
              await user.update({ unsafeMetadata: { ...user.unsafeMetadata, orgId: joined._id, orgName: joined.name } });
            } catch (_) {}
            setShowOrgSetup(false);
          }}
          onSkip={() => {
            if (user?.id) localStorage.setItem(`ni_org_setup_skipped_${user.id}`, '1');
            setShowOrgSetup(false);
          }}
        />
      )}

      {isSignedIn && showProductData && (
        <div style={deviceStyles.overlay}>
          <div style={deviceStyles.modal}>
            <button
              onClick={() => setShowProductData(false)}
              style={deviceStyles.closeButton}
              aria-label="Close product data"
            >
              &times;
            </button>
            <h2 style={deviceStyles.modalTitle}>Product Data</h2>
            {(() => {
              // Built from the record rather than a fixed list, so a product
              // with a different commissioning shape still shows what it has
              // (issue #737).
              const { curated, tests, other } = buildDeviceFields(
                device,
                isCustomer
                  ? { serviceTagNumber: fieldLabels.serviceTagNumber, productNumber: fieldLabels.productNumber }
                  : fieldLabels
              );
              const row = ({ key, label, value }) => (
                <p key={key} style={deviceStyles.tableRow}>
                  <strong style={{ color: colors.textStrong }}>{label}:</strong> {value}
                </p>
              );
              const heading = text => (
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0.85rem 0 0.3rem' }}>
                  {text}
                </div>
              );

              return (
                <>
                  {curated.map(row)}

                  {!isCustomer && tests.length > 0 && (
                    <div>
                      {heading(`Test results (${tests.filter(t => /pass|ok/i.test(t.result)).length}/${tests.length} passed)`)}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ color: colors.textMuted, textAlign: 'left' }}>
                              <th style={{ padding: '0.25rem 0.5rem 0.25rem 0', fontWeight: 600 }}>Check</th>
                              <th style={{ padding: '0.25rem 0.5rem', fontWeight: 600 }}>Result</th>
                              <th style={{ padding: '0.25rem 0', fontWeight: 600 }}>Spec / remark</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tests.map(t => {
                              const passed = /pass|ok/i.test(t.result);
                              return (
                                <tr key={t.key} style={{ borderTop: `1px solid ${colors.panelBorder}` }}>
                                  <td style={{ padding: '0.3rem 0.5rem 0.3rem 0', color: colors.textBody }}>{t.label}</td>
                                  <td style={{ padding: '0.3rem 0.5rem', color: passed ? '#46bb89' : '#f87171', fontWeight: 700, whiteSpace: 'nowrap' }}>{t.result}</td>
                                  <td style={{ padding: '0.3rem 0', color: colors.textMuted }}>{[t.spec, t.remark].filter(Boolean).join(' \u2014 ')}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {!isCustomer && other.length > 0 && (
                    <div>
                      {heading('Production record')}
                      {other.map(row)}
                    </div>
                  )}
                </>
              );
            })()}
            {!isCustomer && device.attributes?.length > 0 && Object.entries(
              device.attributes.reduce((acc, a) => {
                const g = a.group || 'General';
                (acc[g] = acc[g] || []).push(a);
                return acc;
              }, {})
            ).map(([group, rows]) => (
              <div key={group} style={{ marginTop: '0.85rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.3rem' }}>{group}</div>
                {rows.map((a, i) => (
                  <p key={`${a.key}-${i}`} style={deviceStyles.tableRow}>
                    <strong style={{ color: colors.textStrong }}>{a.label || a.key}:</strong> {String(a.value ?? '')}{a.unit ? ` ${a.unit}` : ''}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {challengeTarget && (
        <div style={deviceStyles.overlay}>
          <form
            onSubmit={handleSubmitChallenge}
            style={{
              ...deviceStyles.modal,
              ...deviceStyles.modalNarrow,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}
          >
            <button
              type="button"
              onClick={isChallengeSubmitting ? undefined : closeChallengeModal}
              style={{ ...deviceStyles.closeButton, cursor: isChallengeSubmitting ? 'not-allowed' : 'pointer' }}
              aria-label="Close challenge modal"
              disabled={isChallengeSubmitting}
            >
              &times;
            </button>
            <h3 style={deviceStyles.modalTitle}>Challenge Repair Decision</h3>
            <p style={deviceStyles.modalIntro}>
              Provide additional details for RMA <strong>{challengeTarget.rmaId}</strong>. Our team will review your message.
            </p>
            <label style={{ ...deviceStyles.label, marginTop: '0.5rem' }}>Reason</label>
            <textarea
              value={challengeMessage}
              onChange={event => setChallengeMessage(event.target.value)}
              rows={5}
              placeholder='Explain why the warranty decision should be reconsidered'
              style={{
                ...deviceStyles.input,
                resize: 'vertical',
                minHeight: '120px',
                marginBottom: 0
              }}
            />
            {challengeErrors.message && (
              <div style={{ color: colors.statusYellow, fontSize: '0.85rem' }}>{challengeErrors.message}</div>
            )}
            <label style={deviceStyles.label}>Contact email (optional)</label>
            <input
              type='email'
              value={challengeEmail}
              onChange={event => setChallengeEmail(event.target.value)}
              placeholder='name@example.com'
              style={{ ...deviceStyles.input, marginBottom: 0 }}
            />
            {challengeErrors.email && (
              <div style={{ color: colors.statusYellow, fontSize: '0.85rem' }}>{challengeErrors.email}</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ ...deviceStyles.label, marginBottom: 0 }}>Attachments (optional)</div>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  color: colors.textStrong,
                  borderRadius: '999px',
                  padding: '0.45rem 0.75rem',
                  fontWeight: 600,
                  alignSelf: 'flex-start',
                  border: `1px solid ${colors.panelBorder}`
                }}
              >
                <span>Add attachments</span>
                <input
                  type='file'
                  accept={CHALLENGE_ALLOWED_TYPES.join(',')}
                  multiple
                  onChange={handleChallengeFileChange}
                  style={{ display: 'none' }}
                />
              </label>
              {challengeErrors.attachments && (
                <div style={{ color: colors.statusYellow, fontSize: '0.85rem' }}>{challengeErrors.attachments}</div>
              )}
              {challengeFiles.length > 0 && (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {challengeFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span>
                        {file.name}
                        {formatFileSize(file.size) ? ` (${formatFileSize(file.size)})` : ''}
                      </span>
                      <button
                        type='button'
                        onClick={() => removeChallengeFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: colors.statusYellow,
                          textDecoration: 'underline',
                          cursor: isChallengeSubmitting ? 'not-allowed' : 'pointer'
                        }}
                        disabled={isChallengeSubmitting}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {challengeError && (
              <div style={{
                backgroundColor: 'rgba(236, 33, 36, 0.16)',
                color: '#ffd9d9',
                padding: '0.5rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.9rem',
                border: '1px solid rgba(236, 33, 36, 0.24)'
              }}>
                {challengeError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button
                type='button'
                onClick={closeChallengeModal}
                disabled={isChallengeSubmitting}
                className="ni-button ni-button--ghost"
                style={{ cursor: isChallengeSubmitting ? 'not-allowed' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type='submit'
                disabled={isChallengeSubmitting}
                className="ni-button ni-button--primary"
                style={{ cursor: isChallengeSubmitting ? 'not-allowed' : 'pointer', opacity: isChallengeSubmitting ? 0.7 : 1 }}
              >
                {isChallengeSubmitting ? 'Submitting…' : 'Submit challenge'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showGuestRmaPrompt && (
        <div style={deviceStyles.overlay}>
          <div style={{ ...deviceStyles.modal, maxWidth: '440px', padding: '2rem' }}>
            <button
              type="button"
              onClick={() => setShowGuestRmaPrompt(false)}
              style={deviceStyles.closeButton}
              aria-label="Close"
            >&times;</button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem'
              }}>🔧</div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: colors.textStrong }}>Start a Repair Request</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: colors.textMuted }}>for {device?.productNumber || 'this device'}</p>
              </div>
            </div>

            <div style={{
              borderRadius: 10, border: '1px solid rgba(70,187,137,0.25)',
              background: 'rgba(70,187,137,0.05)', padding: '1rem', marginBottom: '1.25rem'
            }}>
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.82rem', fontWeight: 600, color: colors.textStrong }}>
                Create a free account to unlock:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {[
                  ['📍', 'Track your repair status in real time'],
                  ['📧', 'Automatic email updates at every step'],
                  ['📋', 'Full history of all your RMA cases'],
                  ['🛡️', `${CLAIM_WARRANTY_LABEL} warranty when you claim your device`],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.83rem', color: colors.textBody }}>
                    <span style={{ fontSize: '0.95rem', flexShrink: 0 }}>{icon}</span>
                    {text}
                  </div>
                ))}
              </div>
            </div>

            <SignInButton mode="modal">
              <button className="ni-button ni-button--primary" style={{ width: '100%', marginBottom: '0.65rem' }}>
                Sign in or create account
              </button>
            </SignInButton>

            <button
              type="button"
              onClick={() => { setShowGuestRmaPrompt(false); setShowForm(true); }}
              style={{
                width: '100%', background: 'transparent', border: 'none',
                color: colors.textMuted, fontSize: '0.82rem', cursor: 'pointer',
                padding: '0.4rem', textDecoration: 'underline', textUnderlineOffset: '3px'
              }}
            >
              Continue without an account
            </button>
          </div>
        </div>
      )}

      {/* Confirmation lives outside the form, which unmounts on success. */}
      {!showForm && submittedDeviceCount > 0 && (
        <div style={{
          margin: '0.75rem 0',
          padding: '0.7rem 0.9rem',
          borderRadius: 8,
          background: 'rgba(70,187,137,0.12)',
          border: '1px solid rgba(70,187,137,0.35)',
          color: '#46bb89',
          fontSize: '0.88rem',
        }}>
          RMA submitted successfully
          {submittedDeviceCount > 1
            ? ` for ${submittedDeviceCount} devices — they are handled together under one RMA number.`
            : '.'}
        </div>
      )}

      {showForm && (
        <div style={deviceStyles.overlay}>
          <form
            onSubmit={handleSubmit}
            style={{
              ...deviceStyles.modal,
              width: 'min(96vw, 700px)',
              maxHeight: '90vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0
            }}
          >
            <button
              type="button"
              onClick={() => { setShowForm(false); resetExpectedReturn(); setPartialShipmentWarning(null); }}
              style={deviceStyles.closeButton}
              aria-label="Close RMA form"
            >
              &times;
            </button>
            <h2 style={{ ...deviceStyles.modalTitle, marginBottom: '1.25rem' }}>Start RMA Request</h2>

            {/* Name + Org row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={deviceStyles.label}>Contact Person</label>
                <input value={customerName} onChange={e => setCustomerName(e.target.value)} style={{ ...deviceStyles.input, marginBottom: 0 }} />
              </div>
              <div>
                <label style={deviceStyles.label}>Company</label>
                <input value={contactOrg} onChange={e => setContactOrg(e.target.value)} style={{ ...deviceStyles.input, marginBottom: 0 }} />
              </div>
            </div>

            <label style={deviceStyles.label} htmlFor="rma-contact-email">
              Contact Email <span style={{ color: colors.danger }} aria-hidden="true">*</span>
            </label>
            <input
              id="rma-contact-email"
              type="email"
              required
              value={contactEmail}
              onChange={e => { setContactEmail(e.target.value); if (contactEmailError) setContactEmailError(''); }}
              aria-invalid={contactEmailError ? 'true' : undefined}
              style={{
                ...deviceStyles.input,
                ...(contactEmailError ? { borderColor: colors.danger, marginBottom: '0.25rem' } : null)
              }}
            />
            {contactEmailError && (
              <p style={{ ...deviceStyles.helperText, color: colors.danger, marginTop: 0, marginBottom: '0.75rem' }}>
                {contactEmailError}
              </p>
            )}

            {/* Pre-screening toggle */}
            <div style={{
              border: `1px solid ${preScreened === true ? 'rgba(70,187,137,0.35)' : preScreened === false ? 'rgba(234,179,8,0.3)' : `${colors.panelBorder}`}`,
              borderRadius: '12px',
              padding: '0.85rem 1rem',
              background: preScreened === true ? 'rgba(70,187,137,0.06)' : preScreened === false ? 'rgba(234,179,8,0.05)' : 'rgba(255,255,255,0.03)',
              marginBottom: '0.75rem',
              transition: 'border-color 0.2s, background 0.2s'
            }}>
              <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: colors.textStrong, fontSize: '0.9rem' }}>
                Have you been in contact with North Invent support?
              </p>
              <p style={{ margin: '0 0 0.65rem', color: colors.textMuted, fontSize: '0.82rem', lineHeight: 1.5 }}>
                We recommend discussing your case before submitting — it helps route your RMA faster and avoids unnecessary returns.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setPreScreened(preScreened === true ? null : true)}
                  style={{
                    ...expectedFormStyles.choiceButton,
                    ...(preScreened === true ? expectedFormStyles.choiceButtonActive : {})
                  }}
                >
                  Yes, I have
                </button>
                <button
                  type="button"
                  onClick={() => setPreScreened(preScreened === false ? null : false)}
                  style={{
                    ...expectedFormStyles.choiceButton,
                    ...(preScreened === false ? {
                      backgroundColor: 'rgba(234,179,8,0.15)',
                      borderColor: 'rgba(234,179,8,0.45)',
                      color: '#fbbf24'
                    } : {})
                  }}
                >
                  Not yet
                </button>
              </div>
              {preScreened === true && (
                <textarea
                  value={screeningNotes}
                  onChange={e => setScreeningNotes(e.target.value)}
                  placeholder="Briefly describe what was discussed with support…"
                  style={{ ...deviceStyles.input, minHeight: '72px', marginTop: '0.65rem', marginBottom: 0 }}
                />
              )}
              {preScreened === false && (
                <p style={{ margin: '0.5rem 0 0', color: '#fbbf24', fontSize: '0.82rem', lineHeight: 1.5 }}>
                  Consider reaching out before submitting — support can often resolve the issue or ensure the right parts are sent back.
                </p>
              )}
            </div>

            {/* Issue type + Description */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.75rem', alignItems: 'start', marginBottom: '0.75rem' }}>
              <div>
                <label style={deviceStyles.label}>Issue Type</label>
                <select value={issueType} onChange={e => setIssueType(e.target.value)} className="ni-select" style={{ marginBottom: 0 }}>
                  <option value="hardware">Hardware</option>
                  <option value="software">Software</option>
                  <option value="cosmetic">Cosmetic</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={deviceStyles.label}>Description</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} style={{ ...deviceStyles.input, minHeight: '96px', flex: 1, marginBottom: 0 }} />
              </div>
            </div>

            {/* Additional devices on the same RMA (issue #712) */}
            <div style={{ ...expectedFormStyles.container, marginBottom: '0.75rem' }}>
              <h3 style={expectedFormStyles.sectionTitle}>Sending more than one device?</h3>
              {!showExtraDevices && extraDevices.length === 0 ? (
                <>
                  <p style={{ ...expectedFormStyles.meta, marginBottom: '0.6rem' }}>
                    You can return several devices under this one RMA number — add each one by its service tag.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowExtraDevices(true)}
                    className="ni-button ni-button--ghost"
                    style={{ fontSize: '0.85rem' }}
                  >
                    + Add more devices
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <p style={{ ...expectedFormStyles.meta, margin: 0, flex: 1 }}>
                      All devices below are handled together under one RMA number. Each keeps its own fault details.
                    </p>
                    {extraDevices.length === 0 && (
                      <button
                        type="button"
                        onClick={() => { setShowExtraDevices(false); setExtraTagInput(''); setExtraTagError(''); }}
                        className="ni-button ni-button--ghost"
                        style={{ fontSize: '0.72rem', padding: '0.1rem 0.45rem', flexShrink: 0 }}
                      >
                        Only this device
                      </button>
                    )}
                  </div>

                  <div style={extraDeviceRowStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={extraDeviceIndexStyle}>DEVICE 1</span>
                      <strong style={{ fontSize: '0.88rem' }}>{deviceSerialNumber}</strong>
                      <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{deviceProductNumber}</span>
                      <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginLeft: 'auto' }}>this page</span>
                    </div>
                  </div>

                  {extraDevices.map((d, i) => (
                    <div key={d.serialNumber} style={extraDeviceRowStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={extraDeviceIndexStyle}>DEVICE {i + 2}</span>
                        <strong style={{ fontSize: '0.88rem' }}>{d.serialNumber}</strong>
                        {d.model && <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{d.model}</span>}
                        <button
                          type="button"
                          onClick={() => updateExtraDevice(i, { showDetail: !d.showDetail })}
                          className="ni-button ni-button--ghost"
                          style={{ fontSize: '0.72rem', padding: '0.1rem 0.45rem', marginLeft: 'auto' }}
                        >
                          {d.showDetail ? 'Hide fault' : 'Different fault?'}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeExtraDevice(i)}
                          className="ni-button ni-button--ghost"
                          style={{ fontSize: '0.72rem', padding: '0.1rem 0.45rem' }}
                        >
                          Remove
                        </button>
                      </div>
                      {d.openRmaId && (
                        <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#fbbf24' }}>
                          This device already has an open RMA ({d.openRmaId}). Adding it here may duplicate that case.
                        </p>
                      )}
                      {d.showDetail && (
                        <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem', alignItems: 'start' }}>
                          <div>
                            <label style={deviceStyles.label}>Issue Type</label>
                            <select
                              value={d.issueType || issueType}
                              onChange={e => updateExtraDevice(i, { issueType: e.target.value })}
                              className="ni-select"
                              style={{ marginBottom: 0 }}
                            >
                              <option value="hardware">Hardware</option>
                              <option value="software">Software</option>
                              <option value="cosmetic">Cosmetic</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={deviceStyles.label}>What is wrong with this one?</label>
                            <textarea
                              value={d.description}
                              onChange={e => updateExtraDevice(i, { description: e.target.value })}
                              placeholder="Leave blank to use the description above"
                              style={{ ...deviceStyles.input, minHeight: '60px', flex: 1, marginBottom: 0 }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  <label style={expectedFormStyles.label}>Add a device by service tag</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <input
                      value={extraTagInput}
                      onChange={e => { setExtraTagInput(e.target.value); setExtraTagError(''); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleVerifyExtraDevice(); } }}
                      placeholder="Service tag or serial number"
                      style={{ ...deviceStyles.input, flex: 1, minWidth: 180, marginBottom: 0 }}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyExtraDevice}
                      disabled={extraTagChecking || !extraTagInput.trim()}
                      className="ni-button ni-button--primary"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {extraTagChecking ? 'Checking…' : 'Verify & add'}
                    </button>
                  </div>
                  {extraTagError && (
                    <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#f87171' }}>{extraTagError}</p>
                  )}
                </>
              )}
            </div>

            {/* Customer expectation */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={deviceStyles.label}>What outcome are you expecting? <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <select value={customerExpectation} onChange={e => setCustomerExpectation(e.target.value)} className="ni-select" style={{ width: '100%', marginBottom: 0 }}>
                <option value="">— not specified —</option>
                <option value="warranty_repair">Warranty repair — I believe this should be covered</option>
                <option value="paid_repair">Paid repair — I accept repair costs if needed</option>
                <option value="replacement">Replacement unit</option>
                <option value="assessment_only">Assessment only — just tell me what's wrong</option>
                <option value="return_unrepaired">Return unrepaired if cost is too high</option>
              </select>
            </div>

            {/* Expected Shipment */}
            <div style={{ ...expectedFormStyles.container, marginBottom: '0.75rem' }}>
              <h3 style={expectedFormStyles.sectionTitle}>Expected Shipment</h3>
              <p style={expectedFormStyles.meta}><strong>Device:</strong> {deviceSerialNumber} → {deviceProductNumber}</p>
              <p style={{ ...expectedFormStyles.meta, marginBottom: '0.75rem', color: colors.textBody, fontSize: '0.85rem' }}>
                Please return the <strong>full unit (display + box)</strong>. Sending only part of the device must be arranged with support in advance.
              </p>

              <label style={expectedFormStyles.label}>Shipment type</label>
              <div style={expectedFormStyles.choiceGroup}>
                <button
                  type="button"
                  onClick={() => { setExpectedShipmentType('Full'); setPartialShipmentWarning(null); }}
                  style={{
                    ...expectedFormStyles.choiceButton,
                    ...(expectedShipmentType === 'Full' ? expectedFormStyles.choiceButtonActive : {})
                  }}
                >
                  Full unit (Display + Box)
                </button>
                {[{ value: 'OnlyDisplay', label: 'Only Display (DIU)' }, { value: 'OnlyBox', label: 'Only Box (ELU)' }].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      if (expectedShipmentType === option.value) {
                        setExpectedShipmentType('Full');
                        setPartialShipmentWarning(null);
                      } else {
                        setPartialShipmentWarning(option.value);
                      }
                    }}
                    style={{
                      ...expectedFormStyles.choiceButton,
                      ...(expectedShipmentType === option.value ? expectedFormStyles.choiceButtonActive : {})
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {partialShipmentWarning && expectedShipmentType !== partialShipmentWarning && (
                <div style={{
                  background: 'rgba(234,179,8,0.08)',
                  border: '1px solid rgba(234,179,8,0.35)',
                  borderRadius: '10px',
                  padding: '0.85rem 1rem',
                  marginBottom: '0.75rem'
                }}>
                  <p style={{ margin: '0 0 0.4rem', color: '#fbbf24', fontWeight: 600, fontSize: '0.9rem' }}>
                    Partial return — requires support approval
                  </p>
                  <p style={{ margin: '0 0 0.65rem', color: colors.textMuted, fontSize: '0.85rem', lineHeight: 1.55 }}>
                    Sending only part of the unit must be agreed with North Invent support before the RMA is submitted. Please confirm this has already been arranged.
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => { setExpectedShipmentType(partialShipmentWarning); setPartialShipmentWarning(null); }}
                      style={{
                        background: 'rgba(234,179,8,0.18)',
                        border: '1px solid rgba(234,179,8,0.5)',
                        borderRadius: '6px',
                        padding: '0.4rem 0.9rem',
                        color: '#fbbf24',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Yes, this has been arranged
                    </button>
                    <button
                      type="button"
                      onClick={() => setPartialShipmentWarning(null)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${colors.panelBorder}`,
                        borderRadius: '6px',
                        padding: '0.4rem 0.9rem',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Mounting option */}
              <label style={expectedFormStyles.checkboxLabel}>
                <input type="checkbox" checked={expectedMountIncluded} onChange={e => setExpectedMountIncluded(e.target.checked)} />
                Mounting option included
              </label>
              {expectedMountIncluded && (
                <div style={{ ...expectedFormStyles.mountGrid, marginLeft: '1.5rem' }}>
                  <div style={expectedFormStyles.subField}>
                    <span style={expectedFormStyles.label}>Mount type</span>
                    <div style={expectedFormStyles.choiceGroup}>
                      {MOUNT_TYPE_OPTIONS.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setExpectedMountType(option.value)}
                          style={{
                            ...expectedFormStyles.choiceButton,
                            ...(expectedMountType === option.value ? expectedFormStyles.choiceButtonActive : {})
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={expectedFormStyles.subField}>
                    <span style={expectedFormStyles.label}>With HMI</span>
                    <div style={expectedFormStyles.choiceGroup}>
                      <button type="button" onClick={() => setExpectedMountHmi(true)}
                        style={{ ...expectedFormStyles.choiceButton, ...(expectedMountHmi ? expectedFormStyles.choiceButtonActive : {}) }}>
                        Yes
                      </button>
                      <button type="button" onClick={() => setExpectedMountHmi(false)}
                        style={{ ...expectedFormStyles.choiceButton, ...(!expectedMountHmi ? expectedFormStyles.choiceButtonActive : {}) }}>
                        No
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Accessories */}
              <label style={expectedFormStyles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={expectedAccessoriesIncluded}
                  onChange={e => {
                    setExpectedAccessoriesIncluded(e.target.checked);
                    if (!e.target.checked) setExpectedAccessories({ acPowerCableQty: 0, hdmiCableQty: 0, dpCableQty: 0, touchCableQty: 0 });
                  }}
                />
                Accessories included
              </label>
              {expectedAccessoriesIncluded && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginLeft: '1.5rem' }}>
                  {ACCESSORY_FIELDS.map(({ key, label }) => (
                    <div key={key} style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: `1px solid ${colors.panelBorder}`,
                      borderRadius: '10px',
                      padding: '0.6rem 0.75rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.4rem'
                    }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: colors.textMuted }}>{label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <button
                          type="button"
                          onClick={() => handleExpectedAccessoryChange(key, Math.max(0, expectedAccessories[key] - 1))}
                          style={{
                            width: '30px', height: '30px', border: `1px solid ${colors.panelBorder}`,
                            borderRadius: '6px', background: 'rgba(255,255,255,0.07)',
                            color: colors.textStrong, cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}
                        >−</button>
                        <span style={{ minWidth: '28px', textAlign: 'center', fontWeight: 700, color: colors.textStrong, fontSize: '1rem' }}>
                          {expectedAccessories[key]}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleExpectedAccessoryChange(key, expectedAccessories[key] + 1)}
                          style={{
                            width: '30px', height: '30px', border: `1px solid ${colors.panelBorder}`,
                            borderRadius: '6px', background: 'rgba(255,255,255,0.07)',
                            color: colors.textStrong, cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}
                        >+</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label style={deviceStyles.label}>Images</label>
            <input
              type="file"
              multiple
              onChange={e => setRmaImages(e.target.files)}
              style={{ ...deviceStyles.input, padding: '0.55rem 0.7rem' }}
            />
            <button type="submit" className="ni-button ni-button--primary" style={{ width: '100%', padding: '0.75rem', marginTop: '0.5rem' }}>
              Submit RMA Request
            </button>
            <p style={{ ...deviceStyles.helperText, marginTop: '0.75rem' }}>{formStatus}</p>
          </form>
        </div>
      )}

      {showImprovementForm && (
        <div style={deviceStyles.overlay}>
          <form
            onSubmit={handleImprovementSubmit}
            style={{
              ...deviceStyles.modal,
              ...deviceStyles.modalNarrow,
            }}
          >
            <button
              type="button"
              onClick={() => setShowImprovementForm(false)}
              style={deviceStyles.closeButton}
              aria-label="Close improvement form"
            >
              &times;
            </button>
            <h2 style={deviceStyles.modalTitle}>Suggest Improvement</h2>
            <label style={deviceStyles.label}>Your Name</label>
            <input
              value={impName}
              onChange={e => setImpName(e.target.value)}
              style={deviceStyles.input}
            />
            <label style={deviceStyles.label}>Contact Email</label>
            <input
              type="email"
              value={impEmail}
              onChange={e => setImpEmail(e.target.value)}
              style={deviceStyles.input}
            />
            <label style={deviceStyles.label}>Category</label>
            <select
              value={impCategory}
              onChange={e => setImpCategory(e.target.value)}
              className="ni-select"
              style={{ marginBottom: '0.7rem' }}
            >
              <option value="ui">UI</option>
              <option value="hardware">Hardware</option>
              <option value="performance">Performance</option>
              <option value="feature">Feature</option>
            </select>
            <label style={deviceStyles.label}>Description</label>
            <textarea
              value={impDescription}
              onChange={e => setImpDescription(e.target.value)}
              style={{ ...deviceStyles.input, minHeight: '120px' }}
            />
            <button type="submit" className="ni-button ni-button--primary">
              Submit
            </button>
            <p style={{ ...deviceStyles.helperText, marginTop: '0.75rem' }}>{impStatus}</p>
          </form>
        </div>
      )}

      {ModalComponent && activeRma && (
        <ModalComponent
          rma={activeRma}
          onClose={handleModalClose}
          onSave={handleUpdate}
          {...modalExtraProps}
        />
      )}

      {showDimmingModal && (
        <DimmingDataModal
          files={dimmingFiles}
          profile={device?.dimmingProfile}
          serialNumber={device?.serialNumber}
          onClose={() => setShowDimmingModal(false)}
        />
      )}

      {showClaimLocationModal && (
        <div style={deviceStyles.overlay}>
          <div style={{
            ...deviceStyles.modal,
            width: 'min(92vw, 440px)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h2 style={{ ...deviceStyles.modalTitle, fontSize: '1.6rem' }}>Where is this device installed?</h2>
              <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.88rem', lineHeight: 1.55 }}>
                This helps North Invent understand where devices are commissioned. Optional — you can skip this.
              </p>
            </div>

            <div>
              <label style={deviceStyles.label}>Country</label>
              <select
                value={claimCountry}
                onChange={e => setClaimCountry(e.target.value)}
                className="ni-select"
                style={{ width: '100%' }}
              >
                <option value="">— Select a country —</option>
                <option value="Australia">Australia</option>
                <option value="Austria">Austria</option>
                <option value="Belgium">Belgium</option>
                <option value="Canada">Canada</option>
                <option value="Chile">Chile</option>
                <option value="China">China</option>
                <option value="Denmark">Denmark</option>
                <option value="Finland">Finland</option>
                <option value="France">France</option>
                <option value="Germany">Germany</option>
                <option value="India">India</option>
                <option value="Ireland">Ireland</option>
                <option value="Italy">Italy</option>
                <option value="Japan">Japan</option>
                <option value="Mexico">Mexico</option>
                <option value="Netherlands">Netherlands</option>
                <option value="New Zealand">New Zealand</option>
                <option value="Norway">Norway</option>
                <option value="Poland">Poland</option>
                <option value="Portugal">Portugal</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Singapore">Singapore</option>
                <option value="South Korea">South Korea</option>
                <option value="Spain">Spain</option>
                <option value="Sweden">Sweden</option>
                <option value="Switzerland">Switzerland</option>
                <option value="UAE">UAE</option>
                <option value="UK">UK</option>
                <option value="USA">USA</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <button
              className="ni-button ni-button--primary"
              style={{ width: '100%', padding: '0.65rem' }}
              onClick={() => {
                setShowClaimLocationModal(false);
                handleClaimDevice(claimCountry);
              }}
            >
              {claimCountry ? 'Claim & save location' : 'Claim device'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowClaimLocationModal(false);
                handleClaimDevice('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: colors.textMuted,
                fontSize: '0.8rem',
                cursor: 'pointer',
                alignSelf: 'center',
                padding: '0.1rem 0',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(156,163,175,0.4)'
              }}
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {showWarrantyPopup && (
        <WarrantyModal
          scandate={device.scandate}
          extraMonths={warrantyExtraMonths}
          orgExtraMonths={orgAgreementMonths}
          baseMonths={familyWarrantyMonths}
          companyExtraMonths={companyWarrantyMonths}
          onClose={() => setShowWarrantyPopup(false)}
          isGuestView={isGuestView}
        />
      )}
    </div>
  );
}
